import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Grid, Chip, Button, CircularProgress, Stack } from '@mui/material';
// import verified from "/assets/images/Challan/verified.png";
// import downloadBanner from "assets/images/other/newHeader.svg";
import moment from 'moment';
import DownloadIcon from '@mui/icons-material/Download';
import * as htmlToImage from 'html-to-image';
import download from 'downloadjs';
import { useDevice } from '../../hooks/useResponsive';

const ChallanReceiptContent = React.forwardRef(({ challanData }, ref) => {
  const [downloadLoading, setDownloadLoading] = useState(false);

  const receivedDateFormatted = () =>
    challanData?.recieved_on ? moment(challanData.recieved_on).format('DD/MM/YYYY') : '---';
  const { isMobile } = useDevice();

  const receiptRef = useRef();
  const handleDownload = async () => {
    if (receiptRef.current) {
      setDownloadLoading(true);
      try {
        const dataUrl = await htmlToImage.toJpeg(receiptRef.current, { quality: 0.95 });
        const fileName = `Challan_${challanData?.challan_id || 'receipt'}.jpeg`;
        download(dataUrl, fileName);
      } catch (err) {
        console.error('Failed to download image:', err);
      } finally {
        setDownloadLoading(false);
      }
    }
  };
  return (
    <Box
      ref={ref}
      sx={{
        width: '100%',
        fontFamily: 'Poppins, sans-serif',
        backgroundColor: '#202020',

        p: 3,
        pb: 0,
      }}
    >
      <Box
        component="img"
        src="/assets/images/Challan/banner.png"
        alt="Focus Motors Banner"
        sx={{ maxWidth: '100%', height: 'auto', mb: 3 }}
      />

      <Box className="receipt-box" px={4} ref={receiptRef} sx={{ border: '1px solid #ADB5BD3D', borderRadius: '15px' }}>
        <Typography
          variant="h6"
          align="center"
          sx={{
            fontSize: '16px',
            color: 'white',
            fontWeight: 500,
            my: 4,
          }}
        >
          Payment Receipt
        </Typography>

        <Box sx={{ maxWidth: '340px', mx: 'auto' }}>
          {[
            {
              label: 'Challan ID :',
              value: challanData?.challan_id || '---',
            },
            {
              label: 'Received From :',
              value: `${challanData?.name || '---'} ${challanData?.phone || ''}`,
            },
            // {
            //   label: 'Description :',
            //   value: challanData?.description || '---',
            // },
            {
              label: 'Amount :',
              value: `₹ ${challanData.amount}`,
              bold: true,
            },
            {
              label: 'Received On :',
              value: receivedDateFormatted(),
            },
            {
              label: 'Reference ID :',
              value: challanData?.reference_id || '---',
            },
            {
              label: 'Description :', // Repeated intentionally from original
              value: challanData?.description || '---',
            },
          ].map((field, index) => (
            <Grid container key={index} sx={{ mb: 1 }}>
              <Grid item xs={4.1} md={4}>
                <Typography variant="body2" sx={{ fontWeight: 500, color: '#E0E0E0' }}>
                  {field.label}
                </Typography>
              </Grid>

              <Grid item xs={7.5} md={8}>
                <Box
                  sx={{
                    backgroundColor: '#424242',
                    color: '#E0E0E0',
                    fontWeight: field.bold ? 600 : 400,
                    px: 2,
                    py: 1,
                    borderRadius: '4px',
                    fontSize: '14px',
                    width: '100%', // ✅ Makes value box fixed width
                    whiteSpace: 'pre-wrap', // ✅ Preserve new lines
                    wordBreak: 'break-word', // ✅ Wrap long text
                  }}
                >
                  {field.value}
                </Box>
              </Grid>
            </Grid>
          ))}
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            my: 3,
            mr: '-10px',
          }}
        >
          <Box
            component="img"
            src="/assets/images/Challan/verified.png"
            alt="verified"
            sx={{
              maxWidth: '95px',
              height: '95px',
              objectFit: 'contain',
            }}
          />
        </Box>
      </Box>

      <Box sx={{ px: 2, mt: '6px' }}>
        <Stack justifyContent="space-between" direction={isMobile ? 'column' : 'row'} sx={{ px: 0, py: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', mb: isMobile && 2 }}>
            <Typography color="white" sx={{ mr: 1 }}>
              Status:
            </Typography>

            <Chip
              label={challanData?.status?.charAt(0)?.toUpperCase() + challanData?.status?.slice(1)?.toLowerCase()}
              sx={{
                backgroundColor:
                  challanData?.status === 'PENDING'
                    ? '#6b4b11'
                    : challanData?.status === 'FAILED'
                    ? '#5a1f1f'
                    : '#204d20',
                color:
                  challanData?.status === 'PENDING'
                    ? '#F8971C'
                    : challanData?.status === 'FAILED'
                    ? '#f44336'
                    : '#4caf50',
                borderRadius: '12px',
                px: 2,
                fontWeight: 'bold',
                fontSize: '14px',
                height: '28px',
              }}
            />
          </Box>
          <Stack justifyContent="end" alignItems="end">
            {challanData?.status === 'SUCCESS' && (
              <Button
                size="medium"
                onClick={handleDownload}
                variant="contained"
                // color="warning"
                disabled={downloadLoading}
                sx={{
                  borderRadius: '999px',
                  width: 'fit-content',
                  px: 3,
                  textTransform: 'none',
                  backgroundColor: '#F8971C',
                  color: 'white',
                  boxShadow: 'unset',
                  '&:hover': {
                    backgroundColor: '#e57f00',
                  },
                }}
              >
                {downloadLoading ? (
                  <>
                    <CircularProgress size={20} sx={{ color: 'white', mr: 1 }} />
                    Downloading...
                  </>
                ) : (
                  <>
                    <DownloadIcon style={{ marginRight: '6px' }} />
                    Download Receipt
                  </>
                )}
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
});

ChallanReceiptContent.displayName = 'ChallanReceiptContent';

ChallanReceiptContent.propTypes = {
  challanData: PropTypes.any,
};

export default ChallanReceiptContent;
